import { Button } from "../../ui/components/Button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ColorGradingValues } from "../types";
import { EditorSlider } from "./EditorSlider";

interface ColorGradingEditorProps {
  value: ColorGradingValues;
  onChange: (value: ColorGradingValues) => void;
  onChangeEnd?: (value: ColorGradingValues) => void;
  disabled?: boolean;
}

type ColorGradingTarget = "shadows" | "midtones" | "highlights";

const TARGETS: { key: ColorGradingTarget; label: string }[] = [
  { key: "shadows", label: "Shadows" },
  { key: "midtones", label: "Midtones" },
  { key: "highlights", label: "Highlights" },
];

const SVG_SIZE = 100;
const WHEEL_RADIUS = SVG_SIZE * 0.4; // 40% of SVG size
const WHEEL_RADIUS_PERCENTAGE = 35; // 35% of container

export function ColorGradingEditor({
  value,
  onChange,
  onChangeEnd,
  disabled = false,
}: ColorGradingEditorProps) {
  const [activeTarget, setActiveTarget] = useState<ColorGradingTarget>("midtones");
  const [isDragging, setIsDragging] = useState(false);
  const wheelRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Helper function to convert HSL to hex
  const hslToHex = useCallback((h: number, s: number, l: number) => {
    const hDecimal = l / 100;
    const a = (s * Math.min(hDecimal, 1 - hDecimal)) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = hDecimal - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }, []);

  // Helper function to get position on color wheel (using percentages)
  const getColorWheelPosition = useCallback((hue: number, saturation: number) => {
    const angle = (hue - 90) * (Math.PI / 180); // -90 to start at top
    const distance = saturation * WHEEL_RADIUS_PERCENTAGE;
    return {
      x: 50 + distance * Math.cos(angle),
      y: 50 + distance * Math.sin(angle),
    };
  }, []);

  // Get SVG point from mouse coordinates
  const getSvgPoint = useCallback((clientX: number, clientY: number) => {
    if (!wheelRef.current) {
      return { x: 0.5, y: 0.5 };
    }

    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;

    // Convert to polar coordinates
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const maxDistance = (rect.width / 2) * 0.8; // 80% of radius

    const saturation = Math.min(1, distance / maxDistance);
    let hue = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
    if (hue < 0) {
      hue += 360;
    }

    return { saturation, hue };
  }, []);

  const updateColorFromMouse = useCallback(
    (clientX: number, clientY: number) => {
      if (disabled) {
        return;
      }

      const { hue, saturation } = getSvgPoint(clientX, clientY);
      const currentCircle = value[activeTarget];

      const newValue = {
        ...value,
        [activeTarget]: {
          ...currentCircle,
          hue,
          saturation,
        },
      };
      onChange(newValue);
    },
    [disabled, getSvgPoint, activeTarget, value, onChange]
  );

  const handleWheelClick = useCallback(
    (event: React.MouseEvent) => {
      updateColorFromMouse(event.clientX, event.clientY);
    },
    [updateColorFromMouse]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      setIsDragging(true);
      updateColorFromMouse(event.clientX, event.clientY);
      event.preventDefault();
    },
    [disabled, updateColorFromMouse]
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging || disabled) {
        return;
      }

      updateColorFromMouse(event.clientX, event.clientY);
    },
    [isDragging, disabled, updateColorFromMouse]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging && onChangeEnd) {
      onChangeEnd(value);
    }
    setIsDragging(false);
  }, [isDragging, onChangeEnd, value]);

  // Set up mouse and touch events on the SVG element
  useEffect(() => {
    const svg = wheelRef.current;
    if (!svg) return;

    // Native touch event handlers (not React synthetic events)
    const handleNativeTouchStart = (event: TouchEvent) => {
      if (disabled || event.touches.length !== 1) return;

      event.preventDefault();
      setIsDragging(true);
      const touch = event.touches[0];
      updateColorFromMouse(touch.clientX, touch.clientY);
    };

    const handleNativeTouchMove = (event: TouchEvent) => {
      if (!isDragging || disabled || event.touches.length !== 1) return;

      event.preventDefault();
      const touch = event.touches[0];
      updateColorFromMouse(touch.clientX, touch.clientY);
    };

    const handleNativeTouchEnd = () => {
      if (!isDragging) return;

      if (onChangeEnd) {
        onChangeEnd(value);
      }
      setIsDragging(false);
    };

    // Add native touch listeners with passive: false
    svg.addEventListener("touchstart", handleNativeTouchStart, { passive: false });
    svg.addEventListener("touchmove", handleNativeTouchMove, { passive: false });
    svg.addEventListener("touchend", handleNativeTouchEnd, { passive: false });

    return () => {
      svg.removeEventListener("touchstart", handleNativeTouchStart);
      svg.removeEventListener("touchmove", handleNativeTouchMove);
      svg.removeEventListener("touchend", handleNativeTouchEnd);
    };
  }, [disabled, isDragging, updateColorFromMouse, onChangeEnd, value]);

  // Set up global mouse events for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (event: MouseEvent) => handleMouseMove(event);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const nextTarget = () => {
    const currentIndex = TARGETS.findIndex((t) => t.key === activeTarget);
    const nextIndex = (currentIndex + 1) % TARGETS.length;
    setActiveTarget(TARGETS[nextIndex].key);
  };

  const prevTarget = () => {
    const currentIndex = TARGETS.findIndex((t) => t.key === activeTarget);
    const prevIndex = (currentIndex - 1 + TARGETS.length) % TARGETS.length;
    setActiveTarget(TARGETS[prevIndex].key);
  };

  const currentCircle = value[activeTarget];

  return (
    <div className={`space-y-4 ${disabled ? "pointer-events-none opacity-50" : ""}`}>
      {/* Color Wheel */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onPress={prevTarget} isDisabled={disabled}>
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <span className="font-medium text-sm">
            {TARGETS.find((t) => t.key === activeTarget)?.label}
          </span>

          <Button variant="ghost" size="sm" onPress={nextTarget} isDisabled={disabled}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="relative flex w-full justify-center">
          <div
            ref={containerRef}
            className="relative aspect-square w-[60vw] flex-shrink-0 md:w-[16vw]"
          >
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: Interactive SVG for color wheel */}
            <svg
              ref={wheelRef}
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              className="cursor-move rounded-full touch-manipulation"
              onClick={handleWheelClick}
              onMouseDown={handleMouseDown}
              aria-label="Color wheel - click to select color"
              role="img"
            >
              <defs>
                <radialGradient
                  id={`saturation-gradient-${activeTarget}`}
                  cx="50%"
                  cy="50%"
                  r="41.67%"
                >
                  <stop offset="0%" stopColor="white" />
                  <stop offset="100%" stopColor="transparent" />
                </radialGradient>
                <filter id="blur-filter">
                  <feGaussianBlur stdDeviation="0.5" />
                </filter>
              </defs>

              {/* Color wheel segments */}
              <g filter="url(#blur-filter)">
                {Array.from({ length: 180 }, (_, i) => {
                  const angle = i * 2;
                  const nextAngle = (i + 1) * 2;
                  const startAngle = (angle - 90) * (Math.PI / 180);
                  const endAngle = (nextAngle - 90) * (Math.PI / 180);
                  const innerRadius = 0;
                  const outerRadius = WHEEL_RADIUS;

                  const x1 = SVG_SIZE / 2 + innerRadius * Math.cos(startAngle);
                  const y1 = SVG_SIZE / 2 + innerRadius * Math.sin(startAngle);
                  const x2 = SVG_SIZE / 2 + outerRadius * Math.cos(startAngle);
                  const y2 = SVG_SIZE / 2 + outerRadius * Math.sin(startAngle);
                  const x3 = SVG_SIZE / 2 + outerRadius * Math.cos(endAngle);
                  const y3 = SVG_SIZE / 2 + outerRadius * Math.sin(endAngle);
                  const x4 = SVG_SIZE / 2 + innerRadius * Math.cos(endAngle);
                  const y4 = SVG_SIZE / 2 + innerRadius * Math.sin(endAngle);

                  return (
                    <path
                      key={`segment-${angle}`}
                      d={`M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius} ${outerRadius} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 0 0 ${x1} ${y1} Z`}
                      fill={hslToHex(angle, 100, 50)}
                    />
                  );
                })}
              </g>

              {/* Saturation gradient overlay */}
              <circle
                cx={SVG_SIZE / 2}
                cy={SVG_SIZE / 2}
                r={WHEEL_RADIUS}
                fill={`url(#saturation-gradient-${activeTarget})`}
              />

              {/* Border circle */}
              <circle
                cx={SVG_SIZE / 2}
                cy={SVG_SIZE / 2}
                r={WHEEL_RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-muted-foreground"
              />

              {/* Selected color indicator */}
              <circle
                cx={
                  value[activeTarget]
                    ? getColorWheelPosition(value[activeTarget].hue, value[activeTarget].saturation)
                        .x
                    : 50
                }
                cy={
                  value[activeTarget]
                    ? getColorWheelPosition(value[activeTarget].hue, value[activeTarget].saturation)
                        .y
                    : 50
                }
                r="3"
                fill="white"
                stroke="black"
                strokeWidth="1.5"
                className={`cursor-move touch-manipulation ${!isDragging ? "transition-all duration-150 ease-out" : ""}`}
              />
            </svg>
          </div>
        </div>

        {/* Position indicators */}
        <div className="flex justify-center space-x-2">
          {TARGETS.map((target) => (
            <button
              type="button"
              key={target.key}
              onClick={() => !disabled && setActiveTarget(target.key)}
              disabled={disabled}
              className={`h-2 w-2 rounded-full transition-all ${
                activeTarget === target.key ? "bg-foreground" : "bg-muted-foreground/30"
              } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
              aria-label={`Select ${target.label} for color grading`}
            />
          ))}
        </div>

        {/* Color info */}
        <div className="text-center text-muted-foreground text-xs">
          <p>
            H: {currentCircle.hue.toFixed(0)}° S: {(currentCircle.saturation * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      {/* Blend Slider */}
      <EditorSlider
        label={`${TARGETS.find((t) => t.key === activeTarget)?.label} Blend`}
        value={currentCircle.blend * 100} // Convert to percentage for display
        min={0}
        max={50}
        step={0.01}
        onChange={(blendValue) => {
          const newValue = {
            ...value,
            [activeTarget]: {
              ...currentCircle,
              blend: blendValue / 100, // Convert back to 0-0.5 range
            },
          };
          onChange(newValue);
        }}
        onChangeEnd={(blendValue) => {
          const newValue = {
            ...value,
            [activeTarget]: {
              ...currentCircle,
              blend: blendValue / 100, // Convert back to 0-0.5 range
            },
          };
          if (onChangeEnd) {
            onChangeEnd(newValue);
          }
        }}
        disabled={disabled}
      />
    </div>
  );
}
